import React, { useState } from 'react';
import {
  Paper,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  Avatar,
  Chip,
  Box,
  Badge,
  useTheme,
  alpha,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Person as PersonIcon,
  Group as GroupIcon,
  Schedule as ScheduleIcon,
  AccessTime as AccessTimeIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { format, isSameWeek } from 'date-fns';

const EmployeeList = ({ employees, tasks, shifts, onEmployeeClick }) => {
  // Use tasks if provided, otherwise fall back to shifts for compatibility
  const currentTasks = tasks || shifts;
  const theme = useTheme();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Get tasks count for current week for each employee
  const getEmployeeWeeklyTasks = (employeeId) => {
    const currentWeek = new Date();
    return currentTasks.filter(task => 
      task.employee_id === employeeId && 
      isSameWeek(new Date(task.shift_date), currentWeek)
    ).length;
  };

  // Get total hours for current week for each employee
  const getEmployeeWeeklyHours = (employeeId) => {
    const currentWeek = new Date();
    const employeeTasks = currentTasks.filter(task => 
      task.employee_id === employeeId && 
      isSameWeek(new Date(task.shift_date), currentWeek)
    );

    return employeeTasks.reduce((total, task) => {
      const startTime = new Date(`2000-01-01T${task.start_time}`);
      const endTime = new Date(`2000-01-01T${task.end_time}`);
      const hours = (endTime - startTime) / (1000 * 60 * 60);
      return total + hours;
    }, 0);
  };

  // Get employee initials for avatar
  const getInitials = (employee) => {
    const first = employee.first_name ? employee.first_name[0] : '';
    const last = employee.last_name ? employee.last_name[0] : '';
    return `${first}${last}`.toUpperCase();
  };

  // Get avatar color based on department
  const getAvatarColor = (employee) => {
    return employee.department?.color || theme.palette.grey[400];
  };

  // Pagination logic
  const totalPages = Math.ceil(employees.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedEmployees = employees.slice(startIndex, endIndex);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(event.target.value);
    setCurrentPage(1); // Reset to first page
  };

  return (
    <Paper sx={{ 
      borderRadius: 2,
      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.grey[50], 0.5)})`,
      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
      height: '100%',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`,
      }
    }}>
      <Box sx={{ 
        p: 2, 
        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
        backgroundColor: alpha(theme.palette.primary.main, 0.02)
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupIcon sx={{ color: 'primary.main', fontSize: 20 }} />
            <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main' }}>
              Employees
            </Typography>
            <Chip 
              label={employees.length} 
              size="small" 
              sx={{ 
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                fontWeight: 500
              }}
            />
          </Box>
        </Box>
        
        {/* Pagination Controls */}
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Per Page</InputLabel>
          <Select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            label="Per Page"
            sx={{ fontSize: '0.8rem' }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <List sx={{ 
        flex: 1, 
        overflow: 'auto',
        py: 0,
        '&::-webkit-scrollbar': {
          width: 6
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: alpha(theme.palette.grey[300], 0.2)
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: alpha(theme.palette.primary.main, 0.3),
          borderRadius: 3
        }
      }}>
        {employees.length === 0 ? (
          <ListItem>
            <ListItemText 
              primary={
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No employees found
                </Typography>
              }
            />
          </ListItem>
        ) : (
          paginatedEmployees.map((employee) => {
            const weeklyTasks = getEmployeeWeeklyTasks(employee.id);
            const weeklyHours = getEmployeeWeeklyHours(employee.id);
            const avatarColor = getAvatarColor(employee);

            return (
              <ListItemButton
                key={employee.id}
                onClick={onEmployeeClick ? () => onEmployeeClick(employee) : undefined}
                disabled={!onEmployeeClick}
                sx={{
                  py: 1.5,
                  px: 2,
                  borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`,
                  cursor: onEmployeeClick ? 'pointer' : 'default',
                  '&:hover': onEmployeeClick ? {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04)
                  } : {},
                  '&:last-child': {
                    borderBottom: 'none'
                  },
                  '&.Mui-disabled': {
                    opacity: 0.6
                  }
                }}
              >
                <ListItemAvatar>
                  <Badge
                    badgeContent={weeklyTasks}
                    color="primary"
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.7rem',
                        minWidth: 16,
                        height: 16
                      }
                    }}
                  >
                    <Avatar 
                      sx={{ 
                        width: 40, 
                        height: 40, 
                        backgroundColor: avatarColor,
                        fontSize: '0.9rem',
                        fontWeight: 500
                      }}
                    >
                      {getInitials(employee)}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>

                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                      {employee.first_name} {employee.last_name}
                    </Typography>
                  }
                  secondary={
                    <Box>
                      {employee.department && (
                        <Chip
                          label={employee.department.name}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            backgroundColor: alpha(avatarColor, 0.1),
                            color: avatarColor,
                            mb: 0.5
                          }}
                        />
                      )}
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ScheduleIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {weeklyTasks} tasks this week
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  }
                />
              </ListItemButton>
            );
          })
        )}
      </List>

      {/* Pagination Footer */}
      {employees.length > itemsPerPage && (
        <Box sx={{ 
          p: 2, 
          borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
          backgroundColor: alpha(theme.palette.grey[50], 0.3),
          display: 'flex',
          justifyContent: 'center'
        }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            size="small"
            color="primary"
            sx={{
              '& .MuiPaginationItem-root': {
                fontSize: '0.75rem',
                minWidth: 28,
                height: 28
              }
            }}
          />
        </Box>
      )}
    </Paper>
  );
};

export default EmployeeList;